import React from "react";
import PropTypes from "prop-types";
import {
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";

/**
 * Control auxiliar + boton agregar + lista de lo agregado.
 *
 * El control lo entrega quien la usa (`children`), porque cambia segun el
 * caso: un texto libre para los libros, un select para los niveles. Los
 * elementos siempre llegan normalizados como { nombre, duracion? }.
 *
 * El boton de agregar solo se pinta con `onAgregar`. El buscador de
 * estudiantes no lo necesita: agrega en cuanto se elige a alguien.
 *
 * Con `onAlternar` la lista pasa a modo seleccion: el boton marca y desmarca
 * en vez de borrar, y nada desaparece. Es lo que necesitan los estudiantes de
 * un grupo, donde volver a buscar a alguien por su identificacion es molesto.
 * Sin `onAlternar`, el boton quita el elemento (libros y niveles).
 *
 * Un elemento `fijo` se pinta sin boton: no se puede quitar ni desmarcar.
 * `detalle`, si viene, reemplaza la segunda linea del elemento.
 */
const ListaEditable = ({ items, onAgregar, onQuitar, onAlternar, textoVacio, children }) => {
  const seleccionable = Boolean(onAlternar);
  const marcados = items.filter(({ seleccionado }) => seleccionado).length;

  return (
    <div>
      {/* Fila flexible y no grilla: con columnas 10 + 2 el boton reservaba
          la sexta parte del ancho para un icono de 48 px, y el campo quedaba
          mas corto que los que no llevan boton. Asi el campo toma todo el
          espacio y el boton, solo el suyo. */}
      <div style={{ display: "flex", alignItems: "flex-end" }}>
        {/* minWidth 0 deja que el campo se encoja en vez de desbordar. */}
        <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
        {onAgregar && (
          <IconButton
            // Sin argumentos: el evento del clic no es el elemento.
            onClick={() => onAgregar()}
            aria-label="agregar"
            style={{ marginLeft: 8 }}
          >
            <AddIcon />
          </IconButton>
        )}
      </div>

      <Paper variant="outlined" style={{ marginTop: 8 }}>
        {items.length === 0 ? (
          <Typography variant="body2" color="textSecondary" style={{ padding: 12 }}>
            {textoVacio}
          </Typography>
        ) : (
          <>
            {seleccionable && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ padding: "8px 12px 0 12px" }}
              >
                {`${marcados} de ${items.length} seleccionados`}
              </Typography>
            )}
            <List dense>
              {items.map(({ nombre, duracion, seleccionado, fijo, detalle }, indice) => {
                const apagado = seleccionable && !seleccionado;
                const segundaLinea = detalle || (duracion ? `Duracion: ${duracion}` : null);

                return (
                  <ListItem key={`${nombre}-${indice}`}>
                    <ListItemText
                      primary={nombre}
                      secondary={segundaLinea}
                      primaryTypographyProps={{
                        style: apagado ? { opacity: 0.45 } : undefined,
                      }}
                    />
                    {!fijo && (
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label={apagado ? "incluir" : "quitar"}
                          onClick={() =>
                            seleccionable ? onAlternar(indice) : onQuitar(indice)
                          }
                        >
                          {/* El icono muestra lo que hace el boton, no el estado:
                              X para sacarlo de la solicitud, check para volver a
                              incluirlo. */}
                          {apagado ? (
                            <CheckIcon fontSize="small" color="primary" />
                          ) : (
                            <CloseIcon fontSize="small" />
                          )}
                        </IconButton>
                      </ListItemSecondaryAction>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </>
        )}
      </Paper>
    </div>
  );
};

ListaEditable.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      nombre: PropTypes.string.isRequired,
      duracion: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      seleccionado: PropTypes.bool,
      fijo: PropTypes.bool,
      detalle: PropTypes.string,
    }),
  ).isRequired,
  textoVacio: PropTypes.string.isRequired,
  // Sin esto no se pinta el boton +: el control agrega por su cuenta.
  onAgregar: PropTypes.func,
  // Uno de los dos: quitar del listado, o marcar y desmarcar.
  onQuitar: PropTypes.func,
  onAlternar: PropTypes.func,
};

export default ListaEditable;
